import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View, useWindowDimensions } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { ArrowIcon, Button, Button as Button95, Hourglass, NumberInput, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { EnrichedTextInput, EnrichedTextInputInstance, OnChangeStateEvent } from 'react-native-enriched-html';
import TurndownService from 'turndown';
import showdown from 'showdown';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: '100%',
  },
  inputWrapper: {
    alignItems: 'center',
    width: '100%',
    padding: 10,
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
  },
  numeric: {
    width: 80,
  },
  styleButton: {
    width: 32,
  },
  buttonText: {
    fontWeight: "bold",
  },
  buttonTextItalic: {
    fontWeight: "bold",
    fontStyle: "italic",
  },
  buttonTextUnderline: {
    fontWeight: "bold",
    textDecorationLine: "underline",
  },
  buttonTextStrikethrough: {
    fontWeight: "bold",
    textDecorationLine: "line-through",
  },
});

const turndownService = new TurndownService();

const FONT_SIZE = 18;

export default function AboutYou() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const router = useRouter();
  const dispatch = useDispatch();
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const { edit } = useLocalSearchParams<{ edit?: string }>();
  const ref = useRef<EnrichedTextInputInstance>(null);
  const [state, setState] = useState<OnChangeStateEvent | null>(null);
  const { width, height } = useWindowDimensions();
  const [error, setError] = useState<string>('');
  const [defaultValue, setDefaultValue] = useState<string>('');

  useEffect(() => {
    try {
      if (AppUtils.isValidCommunityMembership(communityMembership)) {
        setDefaultValue(new showdown.Converter().makeHtml(communityMembership.profile.description));
      }
    } catch (e) {
      AppUtilities.handleException(e, "AboutYou.useEffect");
    }
  }, [communityMembership]);

  const onSubmit = useCallback(async () => {
    try {
      showSpinner();

      const html : string = await ref?.current?.getHTML() ?? "";

      // Doesn't work on iOS
      //const markdown = turndownService.turndown(html);
      //if (markdown.length > 0) {

      if (html.length > 0) {
        const response = await AppUtilities.fetchAuthorized(
          "AboutYou.onSubmit",
          "/rabbl/api/set_about",
          "POST",
          {
            domain: domain,
            aboutyou: html,
          }
        );
        const membership: CommunityMembership = ((response as any).body as any).community_membership;
        dispatch(setData(response));
        if (edit && edit.length > 0 && router.canGoBack()) {
          AppUtilities.info("AboutYou.onSubmit popping navigation stack");
          router.back();
        } else {
          router.navigate(AppUtils.getProfileCreateNextPageLink(membership) as RelativePathString);
        }
      } else {
        setError("About you is required.");        
      }
    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(), true);
      AppUtilities.handleException(e, "AboutYou.onSubmit");
    } finally {
      hideSpinner();
    }
  }, [domain, ref]);

  const richTextEditorWidth = Math.min(AppUtils.calculateMaxWidth(width, AppUtils.MaxWidthType.INNER3), 500);

  return (
    <BasicScrollView center={true}>
      <Meta title={"About you"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>
          <AppText>Describe yourself:</AppText>
          <View style={styles.inputWrapper}>
            {/*
            <TextInput
              ref={aboutInput}
              value={value}
              onChangeText={onChange}
              placeholder="About you"
              style={styles.formElement}
              returnKeyType="default"
              onBlur={onBlur}
              onSubmitEditing={handleSubmit(onSubmit, onValidationErrors)}
              multiline={true}
              numberOfLines={6}
              style={AppConstants.STYLES.textInputMultiline}
            />
            */}

            {
              // https://docs.swmansion.com/react-native-enriched-html/api-reference/enriched-text-input
            }
            <Panel variant='raised' style={{ width: richTextEditorWidth + 22, margin: 0, borderTopWidth: 1, borderLeftWidth: 1, borderRightWidth: 1, flexDirection: "row", flexWrap: "wrap" }}>
              <Button variant='menu' active={state?.bold.isActive} style={styles.styleButton} onPress={() => ref.current?.toggleBold()}><AppText style={styles.buttonText}>B</AppText></Button>
              <Button variant='menu' active={state?.italic.isActive} style={styles.styleButton} onPress={() => ref.current?.toggleItalic()}><AppText style={styles.buttonTextItalic}>I</AppText></Button>
              {/*<Button variant='menu' active={state?.underline.isActive} style={styles.styleButton} onPress={() => ref.current?.toggleUnderline()}><AppText style={styles.buttonTextUnderline}>U</AppText></Button>*/}
              {/*<Button variant='menu' active={state?.strikeThrough.isActive} style={styles.styleButton} onPress={() => ref.current?.toggleStrikeThrough()}><AppText style={styles.buttonTextStrikethrough}>S</AppText></Button>*/}
              <Button variant='menu' active={state?.unorderedList.isActive} style={styles.styleButton} onPress={() => ref.current?.toggleUnorderedList()}><AppText style={styles.buttonText}>•</AppText></Button>
              <Button variant='menu' active={state?.orderedList.isActive} style={styles.styleButton} onPress={() => ref.current?.toggleOrderedList()}><AppText style={styles.buttonText}>1</AppText></Button>
              <Button variant='menu' active={state?.blockQuote.isActive} style={styles.styleButton} onPress={() => ref.current?.toggleBlockQuote()}><AppText style={styles.buttonText}>"</AppText></Button>
              {/*<Button variant='menu' active={state?.codeBlock.isActive} style={styles.styleButton} onPress={() => ref.current?.toggleCodeBlock()}><AppText style={styles.buttonText}>💻</AppText></Button>*/}
              <Button variant='menu' active={state?.h1.isActive} style={styles.styleButton} onPress={() => ref.current?.toggleH1()}><AppText>H</AppText></Button>
              <Button variant='menu' active={state?.h2.isActive} style={styles.styleButton} onPress={() => ref.current?.toggleH2()}><AppText>2</AppText></Button>
              <Button variant='menu' active={state?.h3.isActive} style={styles.styleButton} onPress={() => ref.current?.toggleH3()}><AppText>3</AppText></Button>
              <Button variant='menu' active={state?.h4.isActive} style={styles.styleButton} onPress={() => ref.current?.toggleH4()}><AppText>4</AppText></Button>
              <Button variant='menu' active={state?.h5.isActive} style={styles.styleButton} onPress={() => ref.current?.toggleH5()}><AppText>5</AppText></Button>
            </Panel>
            <EnrichedTextInput
              style={{
                minWidth: richTextEditorWidth,
                fontSize: FONT_SIZE,
                minHeight: FONT_SIZE * 4,
                padding: 10,
                backgroundColor: "#ffffff",
                borderWidth: 1,
                borderTopWidth: 0,
              }}
              autoFocus={true}
              placeholder="Describe yourself"
              defaultValue={defaultValue}
              autoCapitalize="none"
              ref={ref}
              onChangeState={e => setState(e.nativeEvent)}
            />
          </View>
          {error.length > 0 && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ {error} ^</AppText>}

          <View style={styles.buttonWrapper}>
            <Button95
              onPress={onSubmit}
            >{edit && edit.length > 0 ? "Save" : "Next"}</Button95>
          </View>
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
